import { equals } from "angular";
import { Stage, Layer } from "konva/lib/Core";
import { RectConfig, Rect } from "konva/lib/shapes/Rect";
import { ISurface, IViewportRect, IWhiteboardState } from "..";
import { IShapeSelectionRenderer } from "./behaviors/selection";
import { WhiteboardEvent, WhiteboardEventObject, addStateEventListener } from "./events";
import { IDraftGoalState, IPosition, ISize, IWhiteboardShapeState, WhiteboardShape } from "./shapes";
import { relativePointerPosition } from "./util";
import { IWhiteboardRenderer } from "./whiteboard-renderer";

const minimapWidth = 220;
const minimapHeight = 137;

export interface MinimapRenderer extends IShapeSelectionRenderer, IWhiteboardRenderer {}
export class MinimapRenderer {
  private stage: Stage;
  private layer: Layer;
  private canvasContainer: HTMLDivElement;
  private minimapViewport: Rect;

  private rendererMinTopLeftPos: IPosition;
  private rendererMaxBottomRightPos: IPosition;
  private rendererViewportRect: IViewportRect;
  private minimapScaleRatio: number;
  private minimapMinTopLeftPos: IPosition;

  constructor(container: HTMLDivElement) {
    this.canvasContainer = document.createElement("div");
    container.appendChild(this.canvasContainer);

    this.stage = new Stage({ container: this.canvasContainer, width: minimapWidth, height: minimapHeight, scaleX: 1, scaleY: 1 });
    this.layer = new Layer();
    this.stage.add(this.layer);

    this.attachStageEvents();
  }

  set rendererMinAndMaxPositions(points: ISurface) {
    this.rendererMinTopLeftPos = points.topLeftPos;
    this.rendererMaxBottomRightPos = points.bottomRightPos;
  }

  set rendererViewportSizeAndPosition(rect: IViewportRect) {
    this.rendererViewportRect = rect;
  }

  destroy(): void {
    this.stage.destroy();
  }

  render(state: IWhiteboardState): void {
    this.calculateMinimapScaleRatio();

    if (!this.minimapScaleRatio) {
      console.warn("Missing minimap scale ratio");
      return;
    }

    const existingShapeIds: Record<string, boolean> = {};

    for (const shapeState of state.shapes) {
      this.renderShape(shapeState);
      existingShapeIds[shapeState.id] = true;
    }

    for (const shape of this.layerShapes()) {
      if (!existingShapeIds[shape.id()]) {
        this.destroyShape(shape);
      }
    }

    this.setMinimapMinAndMaxPositions();
    this.setStagePosition();
    this.renderViewport();

    this.stage.batchDraw();
  }

  addEventListener<EventType extends WhiteboardEvent, UnderlyingEvent>(evt: EventType, listener: (ev: WhiteboardEventObject<EventType, UnderlyingEvent>) => void): this {
    addStateEventListener(this.stage, evt, listener);
    return this;
  }

  private calculateMinimapScaleRatio(): void {
    const { bottomRightPos, topLeftPos } = this.getMinAndMaxPositionBasedOnLayerAndViewport();

    const width = Math.abs(bottomRightPos.x - topLeftPos.x);
    const height = Math.abs(bottomRightPos.y - topLeftPos.y);

    const ratioX = width / minimapWidth;
    const ratioY = height / minimapHeight;

    const ratio = Math.max(ratioX, ratioY);

    this.minimapScaleRatio = ratio < 1 ? 1 : ratio;
  }

  private getMinAndMaxPositionBasedOnLayerAndViewport(): ISurface {
    const viewportMaxX = this.rendererViewportRect.x + this.rendererViewportRect.width;
    const viewportMaxY = this.rendererViewportRect.y + this.rendererViewportRect.height;

    const topLeftPos: IPosition = {
      x: Math.min(this.rendererViewportRect.x, this.rendererMinTopLeftPos.x),
      y: Math.min(this.rendererViewportRect.y, this.rendererMinTopLeftPos.y),
    };

    const bottomRightPos: IPosition = {
      x: Math.max(viewportMaxX, this.rendererMaxBottomRightPos.x),
      y: Math.max(viewportMaxY, this.rendererMaxBottomRightPos.y),
    };

    return {
      topLeftPos,
      bottomRightPos,
    };
  }

  private setMinimapMinAndMaxPositions(): void {
    const { topLeftPos } = this.getMinAndMaxPositionBasedOnLayerAndViewport();

    this.minimapMinTopLeftPos = {
      x: topLeftPos.x / this.minimapScaleRatio,
      y: topLeftPos.y / this.minimapScaleRatio,
    };
  }

  private setStagePosition(): void {
    this.stage.position({
      x: -this.minimapMinTopLeftPos.x,
      y: -this.minimapMinTopLeftPos.y,
    });
  }

  private renderShape(shapeState: IWhiteboardShapeState) {
    const defaultGoalRow = {
      width: 350,
      height: 50,
    };
    const defaultHeadingHeight = 50;
    const placeholderCharactersLength = 13;
    const position = shapeState.position;

    let rectConfig: RectConfig;
    switch (shapeState.type) {
      case "note": {
        rectConfig = this.getRectConfig({ position, size: shapeState.size });
        break;
      }
      case "heading": {
        const width = (shapeState.fontSize / 2) * (shapeState.content.length || placeholderCharactersLength);
        rectConfig = this.getRectConfig({ position, size: { width, height: defaultHeadingHeight } });
        break;
      }
      case "draftGoal": {
        const height = ((shapeState as IDraftGoalState).metrics?.length + 2 || 2) * defaultGoalRow.height;
        rectConfig = this.getRectConfig({ position, size: { width: defaultGoalRow.width, height } });
        break;
      }
      case "goal": {
        rectConfig = this.getRectConfig({ position, size: defaultGoalRow });
        break;
      }
      case "image": {
        rectConfig = this.getRectConfig({ position, size: shapeState.size });
        break;
      }
    }

    const existingShape = this.findShape(shapeState.id);

    if (existingShape && !equals(existingShape.state, shapeState)) {
      existingShape.setAttrs(rectConfig);
      this.layer.batchDraw();
      return;
    }

    const shape = new Rect(rectConfig);
    shape.setId(shapeState.id).addName("shape");
    this.layer.add(shape);
    this.layer.batchDraw();
  }

  private renderViewport() {
    const viewportX = this.rendererViewportRect.x / this.minimapScaleRatio;
    const viewportY = this.rendererViewportRect.y / this.minimapScaleRatio;
    const viewportWidth = this.rendererViewportRect.width / this.minimapScaleRatio;
    const viewportHeight = this.rendererViewportRect.height / this.minimapScaleRatio;

    if (this.minimapViewport) {
      this.minimapViewport.x(viewportX);
      this.minimapViewport.y(viewportY);
      this.minimapViewport.width(viewportWidth);
      this.minimapViewport.height(viewportHeight);
      return;
    }

    this.minimapViewport = new Rect({
      id: "minimapViewport",
      x: viewportX,
      y: viewportY,
      width: viewportWidth,
      height: viewportHeight,
      cornerRadius: 4,
      strokeWidth: 1.5,
      stroke: "#EB597B",
      draggable: true,
      dragBoundFunc: (pos) => {
        const posX = pos.x < 0 ? 0 : pos.x;
        const posY = pos.y < 0 ? 0 : pos.y;
        const viewportWidth = this.minimapViewport.width();
        const viewportHeight = this.minimapViewport.height();
        const maxX = posX + viewportWidth;
        const maxY = posY + viewportHeight;

        if (pos.x < 0) {
          pos.x = 0;
        }

        if (maxX > minimapWidth) {
          pos.x = minimapWidth - viewportWidth;
        }

        if (pos.y < 0) {
          pos.y = 0;
        }

        if (maxY > minimapHeight) {
          pos.y = minimapHeight - viewportHeight;
        }

        const newViewportPosition: IPosition = {
          x: this.minimapViewport.x() * this.minimapScaleRatio,
          y: this.minimapViewport.y() * this.minimapScaleRatio,
        };

        this.stage.fire("state-moveViewport", { newViewportPosition });

        return pos;
      },
    });

    let previousCursorOnHover: string;
    let previousCursonOnDrag: string;

    this.minimapViewport.on("mouseover", () => {
      previousCursorOnHover = document.body.style.cursor;
      document.body.style.cursor = "grab";
    });
    this.minimapViewport.on("mouseout", () => {
      document.body.style.cursor = previousCursorOnHover;
      previousCursorOnHover = undefined;
    });

    this.minimapViewport.on("dragstart", function () {
      previousCursonOnDrag = document.body.style.cursor;
      document.body.style.cursor = "grabbing";
    });

    this.minimapViewport.on("dragend", function () {
      document.body.style.cursor = previousCursonOnDrag;
      previousCursonOnDrag = undefined;
    });

    this.layer.add(this.minimapViewport);
    this.layer.batchDraw();
  }

  private attachStageEvents(): void {
    this.stage.on("click", () => {
      const relativePointerPos = relativePointerPosition(this.stage);
      const viewportWidth = this.minimapViewport.width();
      const viewportHeight = this.minimapViewport.height();

      let newViewportX = relativePointerPos.x - viewportWidth / 2;
      let newViewportY = relativePointerPos.y - viewportHeight / 2;
      const maxX = this.minimapMinTopLeftPos.x + minimapWidth;
      const maxY = this.minimapMinTopLeftPos.y + minimapHeight;

      if (newViewportX < this.minimapMinTopLeftPos.x) {
        newViewportX = this.minimapMinTopLeftPos.x;
      }

      if (newViewportX + viewportWidth > maxX) {
        newViewportX = maxX - viewportWidth;
      }

      if (newViewportY < this.minimapMinTopLeftPos.y) {
        newViewportY = this.minimapMinTopLeftPos.y;
      }

      if (newViewportY + viewportHeight > maxY) {
        newViewportY = maxY - viewportHeight;
      }

      const newViewportPosition: IPosition = {
        x: newViewportX * this.minimapScaleRatio,
        y: newViewportY * this.minimapScaleRatio,
      };

      this.stage.fire("state-moveViewport", { newViewportPosition });
    });
  }

  private getRectConfig(shape: { position: IPosition; size: ISize }): RectConfig {
    return {
      x: shape.position.x / this.minimapScaleRatio,
      y: shape.position.y / this.minimapScaleRatio,
      width: shape.size.width / this.minimapScaleRatio,
      height: shape.size.height / this.minimapScaleRatio,
      fill: "#DBE1EA",
      cornerRadius: 1,
      listening: false,
    };
  }

  private layerShapes() {
    return [...this.layer.find<WhiteboardShape>(".shape")];
  }

  private findShape(id: string): WhiteboardShape {
    return this.layer.findOne(`#${id}`);
  }

  private destroyShape(shape: WhiteboardShape) {
    shape.destroy();
  }
}
